/**
 * Shared Axios instance and utility functions for all API modules.
 *
 * Authentication model:
 *  - JWT access + refresh tokens live in HttpOnly cookies set by the backend
 *    at login (`migratr_access`, `migratr_refresh`).  The browser attaches
 *    them automatically on every same-origin request, so we set
 *    `withCredentials: true`.
 *  - CSRF protection uses the double-submit pattern: the backend also issues
 *    a non-HttpOnly `migratr_csrf` cookie at login that we echo back in
 *    the `X-CSRF-Token` header on every unsafe verb (POST/PUT/PATCH/DELETE).
 *  - On 401 (non-login/non-refresh) we call /auth/refresh (cookie-driven);
 *    if that fails we redirect to /login.
 *  - A queue-based mechanism prevents multiple simultaneous refresh calls.
 */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import {
  USER_KEY,
  TENANT_SCOPED_KEYS,
  LEGACY_TOKEN_KEY,
  LEGACY_REFRESH_TOKEN_KEY,
  REMEMBER_ME_KEY,
} from '../utils/storageKeys';

const resolvedEnvBase =
  process.env.REACT_APP_API_URL &&
  !process.env.REACT_APP_API_URL.includes('localhost') &&
  !process.env.REACT_APP_API_URL.includes('127.0.0.1')
    ? process.env.REACT_APP_API_URL
    : undefined;

const isLocalDev = typeof window !== 'undefined' && window.location.port === '3000';

// In local dev mode the CRA dev-server proxies /api/* to the nginx gateway
// (see setupProxy.js).  Using same-origin requests ('') avoids Chrome's
// 6-connection-per-origin HTTP/1.1 limit that starves API calls when SSE
// streams consume cross-origin connection slots.
const defaultGatewayBase = resolvedEnvBase || (
  typeof window !== 'undefined'
    ? (isLocalDev
      ? ''  // same-origin — dev-server proxy handles routing to :80
      : `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`)
    : 'http://localhost:80'
);

export const SERVICE_BASES = {
  ingestion: process.env.REACT_APP_INGESTION_URL || defaultGatewayBase,
  mapping: process.env.REACT_APP_MAPPING_URL || defaultGatewayBase,
  loader: process.env.REACT_APP_LOADER_URL || defaultGatewayBase,
  orchestrator: process.env.REACT_APP_ORCHESTRATOR_URL || defaultGatewayBase,
  gateway: defaultGatewayBase,
};

export const DEFAULT_PROJECT_ID = process.env.REACT_APP_DEFAULT_PROJECT_ID || '';

export const DEFAULT_TENANT_ID = process.env.REACT_APP_DEFAULT_TENANT_ID || 'default-tenant';

export const ensureProjectId = (projectId?: string): string => {
  if (projectId && projectId.trim().length > 0) {
    return projectId.trim();
  }
  if (DEFAULT_PROJECT_ID) {
    console.warn(
      'Falling back to REACT_APP_DEFAULT_PROJECT_ID for project-aware API call. Configure project selection in UI for better accuracy.'
    );
    return DEFAULT_PROJECT_ID;
  }
  throw new Error(
    'Project ID is required for this operation. Set REACT_APP_DEFAULT_PROJECT_ID or provide a projectId.'
  );
};

export const resolveServiceBase = (path: string): string => {
  if (path.startsWith('/api/v2/mapping-canvas')) {
    return SERVICE_BASES.mapping;
  }
  if (path.startsWith('/api/v1/agents')) {
    return SERVICE_BASES.orchestrator;
  }
  if (
    path.startsWith('/api/v1/mappings') ||
    path.startsWith('/api/v1/transformation-rules') ||
    path.startsWith('/api/v1/schemas') ||
    path.startsWith('/api/v1/schema-sets') ||
    path.startsWith('/api/v1/projects') ||
    path.startsWith('/api/v1/schema-drafts') ||
    path.startsWith('/api/v1/recipes') ||
    path.startsWith('/api/v1/execution-runs') ||
    path.startsWith('/api/v1/rule-templates')
  ) {
    return SERVICE_BASES.mapping;
  }
  if (path.startsWith('/api/v1/loader')) {
    return SERVICE_BASES.loader;
  }
  return SERVICE_BASES.ingestion;
};

export const buildUrl = (path: string): string => {
  return `${resolveServiceBase(path)}${path}`;
};

/**
 * Shared Axios instance used by all API domain modules.
 *
 * `withCredentials: true` is required so the browser attaches the HttpOnly
 * auth cookies on every request.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: defaultGatewayBase,
  withCredentials: true,
  // 30s default timeout.  The previous 300s (5 min) caused HTTP/1.1
  // connection pool exhaustion: timed-out requests held connection slots
  // for 5 minutes, blocking all other requests to the same origin.
  timeout: 30_000,
});

interface RequestConfigWithAuthFlags extends AxiosRequestConfig {
  skipAuthRefresh?: boolean;
}

// ---------------------------------------------------------------------------
// CSRF double-submit — read non-HttpOnly migratr_csrf cookie, echo in header
// ---------------------------------------------------------------------------

const CSRF_COOKIE_NAME = 'migratr_csrf';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Read a cookie value by name, or null if not present. */
export const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const parts = document.cookie.split('; ');
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
};

// Attach the CSRF token on every unsafe verb.
apiClient.interceptors.request.use(
  (config) => {
    const method = (config.method || 'get').toUpperCase();
    if (UNSAFE_METHODS.has(method)) {
      const csrf = readCookie(CSRF_COOKIE_NAME);
      if (csrf) {
        config.headers = config.headers || {};
        config.headers[CSRF_HEADER_NAME] = csrf;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Silent token refresh + retry on 401
// ---------------------------------------------------------------------------

let isRefreshing = false;
// Queue of callbacks waiting for the refresh to complete.  The argument
// indicates whether the refresh succeeded (true) or not (false); we no
// longer have tokens on the JS side, so there is nothing else to hand back.
let refreshSubscribers: Array<(ok: boolean) => void> = [];

const notifyRefreshSubscribers = (ok: boolean) => {
  refreshSubscribers.forEach((cb) => cb(ok));
  refreshSubscribers = [];
};

const subscribeToRefresh = (callback: (ok: boolean) => void) => {
  refreshSubscribers.push(callback);
};

/** Attempt to refresh the session cookie-to-cookie.  Returns success flag. */
export const tryRefreshToken = async (): Promise<boolean> => {
  try {
    // Body is empty — the backend reads the migratr_refresh HttpOnly cookie.
    // Using a bare axios call (not apiClient) to avoid the 401 interceptor
    // recursion.  withCredentials is required so the refresh cookie flows.
    await axios.post(
      `${defaultGatewayBase}/api/v1/auth/refresh`,
      {},
      { timeout: 10_000, withCredentials: true },
    );
    return true;
  } catch {
    return false;
  }
};

const clearSessionAndRedirect = async () => {
  // Best-effort server-side cookie clear.  Even if this fails, we still
  // wipe the UI state and navigate to /login.
  try {
    await axios.post(
      `${defaultGatewayBase}/api/v1/auth/logout`,
      {},
      { timeout: 5_000, withCredentials: true },
    );
  } catch {
    /* noop — we're logging out anyway */
  }

  [localStorage, sessionStorage].forEach((s) => {
    s.removeItem(USER_KEY);
  });
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  sessionStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  localStorage.removeItem(REMEMBER_ME_KEY);
  for (const key of TENANT_SCOPED_KEYS) {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RequestConfigWithAuthFlags & { _retry?: boolean };
    if (originalRequest?.skipAuthRefresh) {
      return Promise.reject(error);
    }

    if (error?.response?.status !== 401) {
      return Promise.reject(error);
    }

    // Don't attempt refresh for unauthenticated auth endpoints — the 401
    // there is a legitimate credential error, not an expired session.
    const url = originalRequest?.url || '';
    const isPublicAuthUrl =
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/invite') ||
      url.includes('/auth/forgot-password') ||
      url.includes('/auth/reset-password');
    if (isPublicAuthUrl || url.includes('/auth/refresh')) {
      if (url.includes('/auth/refresh')) {
        void clearSessionAndRedirect();
      }
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      void clearSessionAndRedirect();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Another refresh is already in-flight — queue this request.
      return new Promise((resolve, reject) => {
        subscribeToRefresh((ok) => {
          if (ok) {
            resolve(apiClient(originalRequest));
          } else {
            reject(error);
          }
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshed = await tryRefreshToken();
    isRefreshing = false;

    if (!refreshed) {
      notifyRefreshSubscribers(false);
      void clearSessionAndRedirect();
      return Promise.reject(error);
    }

    notifyRefreshSubscribers(true);
    return apiClient(originalRequest);
  },
);

export const getWithoutAuthRefresh = <T = unknown>(url: string, config: RequestConfigWithAuthFlags = {}) =>
  apiClient.get<T>(url, { ...config, skipAuthRefresh: true } as RequestConfigWithAuthFlags);

// ---------------------------------------------------------------------------
// Number parsing utilities
// ---------------------------------------------------------------------------

export const parseNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return NaN;
    }
    return Number(trimmed);
  }
  if (typeof value === 'bigint') {
    return Number(value);
  }
  return NaN;
};

export const coerceNumber = (value: unknown, fallback = 0): number => {
  const numeric = parseNumber(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

export const coerceOptionalNumber = (value: unknown): number | undefined => {
  const numeric = parseNumber(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

export const bytesToMegabytes = (value: unknown): number => {
  const bytes = coerceNumber(value, 0);
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return 0;
  }
  const megabytes = bytes / (1024 * 1024);
  return Number.isFinite(megabytes) ? megabytes : 0;
};